<script setup lang="ts">
    import type { TrainingsListingItem } from '~/types/academy/baseTraining';
    import { enrichTeaserBadges, removeDuplicateBadges } from '~/utils/academyUtils';
    import { useStoryblokUrl } from '~/composables/useStoryblokUrl';
    import { parseStoryblokDateUTC } from '~/utils/date';
    import { useI18n } from 'vue-i18n';

    const { item } = defineProps<{
        item: TrainingsListingItem;
    }>();

    const { locale } = useI18n();

    const data = computed(() => ({
        ...item,
        badges: removeDuplicateBadges(enrichTeaserBadges(item))
    }));

    const formatDate = (date: Date | string, options: Intl.DateTimeFormatOptions = { year: 'numeric' }) => {
        /**
         * Storyblok stores dates (`startsAt` and `endsAt`) in this format: `2026-01-01 00:00`. This is in UTC.
         *
         * Parse the string explicitly as UTC. `new Date('2026-01-01 00:00')` would otherwise be interpreted as
         * local time, producing different results on server vs. client.
         */
        const parsedDate = typeof date === 'string' ? parseStoryblokDateUTC(date) : date;
        if (!parsedDate) return '';

        // Format with explicit timezone so the rendered date is identical regardless of the runtime (server vs. client) timezone.
        return parsedDate.toLocaleDateString(locale.value, {
            timeZone: 'Europe/Berlin',
            month: '2-digit',
            day: '2-digit',
            ...options
        });
    };
</script>

<template>
    <NuxtLink
        :href="useStoryblokUrl(data.ctaUrl)"
        class="teaser-card-preview"
        :aria-label="$t('academy.buttonLabel.learnMore')"
    >
        <TeaserImage
            :item="data"
            square
            class="teaser-image"
        />
        <div class="teaser-content">
            <TeaserBadges
                v-if="data.badges[0]"
                :badges="[data.badges[0]]"
            />

            <div
                v-if="item.type === 'event'"
                class="teaser-details"
            >
                <span v-if="item.startsAt">{{
                    formatDate(item.startsAt, item.sameDay ? { year: 'numeric' } : {})
                }}</span>
                <span v-if="item.endsAt && !item.sameDay">–{{ formatDate(item.endsAt) }}</span>
                <span v-if="item.location"> | {{ item.location }}</span>
            </div>
            <h3 class="teaser-title">
                {{ data.title }}
            </h3>
            <div class="details-action">
                <SharedIconButton
                    variant="ghost"
                    size="md"
                    icon="lucide:circle-arrow-right"
                />
            </div>
        </div>
    </NuxtLink>
</template>

<style scoped lang="scss">
    .teaser-card-preview {
        display: grid;
        grid-template-columns: 25% 1fr;
        width: 100%;
        border: 0;
        border-radius: $border-radius-s;
        overflow: hidden;
        position: relative;

        @media (max-width: $br-xs) {
            max-width: 100%;
        }

        &:hover {
            .teaser-title {
                text-decoration: underline;
            }
        }

        .teaser-title {
            font-size: $font-sm;
            letter-spacing: 0;
            margin-right: $spacing--2;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            max-width: 100%;
            min-width: 0;
        }

        .teaser-image {
            padding: 0; /* override padding from <TeaserImage /> */
        }

        .teaser-content {
            min-width: 0;
            padding-left: $spacing--05;
        }

        .teaser-details {
            font-size: $font-xs;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            max-width: 100%;
        }

        .details-action {
            position: absolute;
            bottom: 0;
            right: 0;
            align-items: end;
            justify-content: end;
            display: flex;
        }
    }
</style>
